import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { Combobox } from '@buildoutinc/blueprint-react/ui/Combobox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import debounce from 'lodash/debounce';

import { get } from 'helpers/api';

import EmailRecipient from './email_recipient';
import type { Recipient } from './email_recipient';

interface SearchedContact {
  id: number;
  name?: string;
  displayName?: string;
  email?: string;
}

interface Props {
  id?: string;
  recipients: Recipient[];
  suggestions?: Recipient[];
  onChange: (recipients: Recipient[]) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_QUERY_LENGTH = 2;

export function sameEmail(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export default function EmailRecipientInput({ id, recipients, suggestions = [], onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Recipient[]>([]);
  const requestId = useRef(0);

  const searchContacts = useMemo(() => debounce(async (value: string) => {
    const currentId = ++requestId.current;
    try {
      const contacts: SearchedContact[] = await get('/backoffice/contacts/search', { params: { query: value } });
      if (currentId !== requestId.current) return;
      setResults(
        contacts
          .filter(contact => contact.email)
          .map(contact => ({ name: contact.displayName || contact.name, email: contact.email as string }))
      );
    } catch {
      if (currentId === requestId.current) setResults([]);
    }
  }, 300), []);

  useEffect(() => () => searchContacts.cancel(), [searchContacts]);

  const handleInputChange = (value: string) => {
    setQuery(value);

    if (value.trim().length < MIN_QUERY_LENGTH) {
      searchContacts.cancel();
      setResults([]);
      return;
    }

    void searchContacts(value.trim());
  };

  const trimmed = query.trim();
  const lowered = trimmed.toLowerCase();
  const matchingSuggestions = suggestions.filter(
    suggestion => suggestion.email.toLowerCase().includes(lowered) || suggestion.name?.toLowerCase().includes(lowered)
  );
  const options = [
    ...matchingSuggestions,
    ...results.filter(result => !matchingSuggestions.some(suggestion => sameEmail(suggestion.email, result.email))),
  ].filter(option => !recipients.some(recipient => sameEmail(recipient.email, option.email)));

  const newRecipient: Recipient | null =
    EMAIL_PATTERN.test(trimmed) &&
    !recipients.some(recipient => sameEmail(recipient.email, trimmed)) &&
    !options.some(option => sameEmail(option.email, trimmed))
      ? { email: trimmed }
      : null;

  const items = newRecipient ? [newRecipient, ...options] : options;

  return (
    <Combobox
      autoHighlight
      filter={null}
      isItemEqualToValue={(a: Recipient, b: Recipient) => sameEmail(a.email, b.email)}
      items={items}
      itemToStringLabel={(recipient: Recipient) => recipient.name || recipient.email}
      multiple
      value={recipients}
      onInputValueChange={handleInputChange}
      onValueChange={(value: Recipient[]) => {
        onChange(value);
        setQuery('');
        setResults([]);
      }}
    >
      <Combobox.Chips
        className="email-recipient-chips d-flex align-items-center gap-1 border-0 p-0 bg-transparent"
        id="log-email"
      >
        {recipients.map(recipient => (
          <Combobox.Chip
            aria-label={recipient.email}
            className="email-recipient mw-100"
            key={recipient.email}
            showRemove={false}
          >
            <EmailRecipient recipient={recipient} />
            {/* Blueprint's own remove button has no accessible name. */}
            <ComboboxPrimitive.ChipRemove aria-label={`Remove ${recipient.email}`} className="combobox-chip-remove">
              <FontAwesomeIcon icon={faXmark} />
            </ComboboxPrimitive.ChipRemove>
          </Combobox.Chip>
        ))}
        <Combobox.Input
          id={id}
          placeholder="Search contacts or type an email address..."
          render={
            <input
              className="border-0 bg-transparent p-0 flex-grow-1 text-truncate"
              style={{ outline: 'none', minWidth: '6rem' }}
            />
          }
        />
      </Combobox.Chips>

      <Combobox.Content>
        <Combobox.Empty>No contacts found</Combobox.Empty>
        <Combobox.List>
          {items.map(item => (
            <Combobox.Item key={item.email} value={item}>
              {item === newRecipient
                ? <span>Add "{item.email}"</span>
                : <EmailRecipient recipient={item} />}
            </Combobox.Item>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}
